import { request as baseRequest, shopifyAppProxyRequest } from '../utils/request';
import {
  LoginResponse,
  Method,
  PasswordlessCodeResponse,
  PasswordlessOptions,
  PasswordlessValidateResponse,
  RequestHeaders,
  RequestOptions,
  Session,
} from '../types';
import { getOptions } from '../utils/options';
import { RECHARGE_ADMIN_URL } from '../constants/api';

export async function loginShopifyAppProxy(): Promise<Session> {
  const { storefrontAccessToken } = getOptions();
  const headers: RequestHeaders = {};
  if (storefrontAccessToken) {
    headers['X-Recharge-Storefront-Access-Token'] = storefrontAccessToken;
  }
  const response = await shopifyAppProxyRequest<LoginResponse>('get', '/access', { headers });

  return { apiToken: response.api_token, customerId: response.customer_id, message: response.message };
}

/** @deprecated Use `loginWithShopifyStorefront` instead */
export async function loginShopifyApi(
  shopifyStorefrontToken: string,
  shopifyCustomerAccessToken?: string
): Promise<Session | null> {
  return loginWithShopifyStorefront(shopifyStorefrontToken, shopifyCustomerAccessToken);
}

export async function loginWithShopifyStorefront(
  shopifyStorefrontToken: string,
  shopifyCustomerAccessToken?: string
): Promise<Session | null> {
  if (!shopifyStorefrontToken) {
    throw new Error('Shopify storefront token is required');
  }
  const { storeIdentifier } = getOptions();
  if (!storeIdentifier) {
    throw new Error('InitRecharge has not been called and/or no store identifier has been defined.');
  }
  const {
    api_token: apiToken,
    customer_id: customerId,
    message,
  } = await rechargeAdminRequest<LoginResponse>('post', '/shopify_storefront_access', {
    data: {
      customer_token: shopifyCustomerAccessToken,
      storefront_token: shopifyStorefrontToken,
      shop_url: storeIdentifier,
    },
  });

  return apiToken ? { apiToken, customerId, message } : null;
}

export async function loginWithShopifyCustomerAccount(shopifyCustomerAccessToken?: string): Promise<Session | null> {
  const { storeIdentifier } = getOptions();
  if (!storeIdentifier) {
    throw new Error('InitRecharge has not been called and/or no store identifier has been defined.');
  }
  const {
    api_token: apiToken,
    customer_id: customerId,
    message,
  } = await rechargeAdminRequest<LoginResponse>('post', '/shopify_customer_account_api_access', {
    data: {
      customer_token: shopifyCustomerAccessToken,
      shop_url: storeIdentifier,
    },
  });

  return apiToken ? { apiToken, customerId, message } : null;
}

export async function sendPasswordlessCode(email: string, options: PasswordlessOptions = {}): Promise<string> {
  if (!email) {
    throw new Error('Email is required.');
  }
  const { storeIdentifier } = getOptions();
  if (!storeIdentifier) {
    throw new Error('InitRecharge has not been called and/or no store identifier has been defined.');
  }
  const response = await rechargeAdminRequest<PasswordlessCodeResponse>('post', '/attempt_login', {
    data: {
      email,
      shop: storeIdentifier,
      ...options,
    },
  });

  if (response.errors) {
    throw new Error(response.errors);
  }
  return response.session_token;
}

export async function sendPasswordlessCodeAppProxy(email: string, options: PasswordlessOptions = {}): Promise<string> {
  if (!email) {
    throw new Error('Email is required.');
  }
  const { storefrontAccessToken } = getOptions();
  const headers: RequestHeaders = {};
  if (storefrontAccessToken) {
    headers['X-Recharge-Storefront-Access-Token'] = storefrontAccessToken;
  }
  const response = await shopifyAppProxyRequest<PasswordlessCodeResponse>('post', '/attempt_login', {
    data: {
      email,
      ...options,
    },
    headers,
  });

  if (response.errors) {
    throw new Error(response.errors);
  }
  return response.session_token;
}

export async function validatePasswordlessCode(email: string, session_token: string, code: string): Promise<Session> {
  if (!email) {
    throw new Error('Email is required.');
  }
  if (!session_token) {
    throw new Error('Session token is required.');
  }
  if (!code) {
    throw new Error('Code is required.');
  }
  const { storeIdentifier } = getOptions();
  if (!storeIdentifier) {
    throw new Error('InitRecharge has not been called and/or no store identifier has been defined.');
  }
  const response = await rechargeAdminRequest<PasswordlessValidateResponse>('post', '/validate_login', {
    data: {
      code,
      email,
      session_token,
      shop: storeIdentifier,
    },
  });

  if (response.errors) {
    throw new Error(response.errors);
  }
  return { apiToken: response.api_token, customerId: response.customer_id };
}

export async function validatePasswordlessCodeAppProxy(
  email: string,
  session_token: string,
  code: string
): Promise<Session> {
  if (!email) {
    throw new Error('Email is required.');
  }
  if (!session_token) {
    throw new Error('Session token is required.');
  }
  if (!code) {
    throw new Error('Code is required.');
  }
  const { storefrontAccessToken } = getOptions();
  const headers: RequestHeaders = {};
  if (storefrontAccessToken) {
    headers['X-Recharge-Storefront-Access-Token'] = storefrontAccessToken;
  }
  const response = await shopifyAppProxyRequest<PasswordlessValidateResponse>('post', '/validate_login', {
    data: {
      code,
      email,
      session_token,
    },
    headers,
  });

  if (response.errors) {
    throw new Error(response.errors);
  }
  return { apiToken: response.api_token, customerId: response.customer_id };
}

function getCustomerParams() {
  const { customerHash: devCustomerHash } = getOptions();
  const { pathname, search } = window.location;
  const urlParams = new URLSearchParams(search);
  const token = urlParams.get('token');
  const subpaths = pathname.split('/').filter(Boolean);
  const portalIndex = subpaths.findIndex(path => path === 'portal');
  const customerHash = portalIndex !== -1 ? subpaths[portalIndex + 1] : devCustomerHash;

  // make sure the URL contained the data we need
  if (!token || !customerHash) {
    throw new Error('Not in context of Recharge Customer Portal or URL did not contain correct params');
  }
  return { customerHash, token };
}

export async function loginCustomerPortal(): Promise<Session> {
  const { customerHash, token } = getCustomerParams();
  const { storeIdentifier } = getOptions();
  const response = await rechargeAdminRequest<LoginResponse>('post', `/customers/${customerHash}/access`, {
    data: {
      token,
      shop: storeIdentifier,
    },
  });

  return { apiToken: response.api_token, customerId: response.customer_id };
}

async function rechargeAdminRequest<T>(
  method: Method,
  url: string,
  { id, query, data, headers }: RequestOptions = {}
): Promise<T> {
  const { environment, environmentUri, storefrontAccessToken, appName, appVersion } = getOptions();
  const rechargeBaseUrl = RECHARGE_ADMIN_URL(environment, environmentUri);
  const reqHeaders: RequestHeaders = {
    ...(storefrontAccessToken ? { 'X-Recharge-Storefront-Access-Token': storefrontAccessToken } : {}),
    ...(appName ? { 'X-Recharge-Sdk-App-Name': appName } : {}),
    ...(appVersion ? { 'X-Recharge-Sdk-App-Version': appVersion } : {}),
    'X-Recharge-Sdk-Version': '__SDK_VERSION__',
    ...(headers ? headers : {}),
  };

  return baseRequest<T>(method, `${rechargeBaseUrl}${url}`, { id, query, data, headers: reqHeaders });
}
